/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

export default function Messages({ companyId, onMessagesRead }) {
  const [threads, setThreads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [releases, setReleases] = useState([]);
  const [activeTab, setActiveTab] = useState('messages');

  useEffect(() => {
    if (!companyId) return;
    fetchAll();
  }, [companyId]);

  async function fetchAll() {
    const { data: emps } = await supabase.from('employees').select('*').eq('company_id', companyId);
    setEmployees(emps || []);

    const { data: msgs } = await supabase.from('messages').select('*').eq('company_id', companyId).order('created_at', { ascending: false });
    if (msgs) {
      // Group by employee
      const byEmp = {};
      msgs.forEach(m => {
        const key = m.sender_id || m.recipient_id;
        if (!byEmp[key]) byEmp[key] = [];
        byEmp[key].push(m);
      });
      setThreads(Object.entries(byEmp).map(([empId, msgs]) => ({ empId: parseInt(empId), msgs, latest: msgs[0] })));
    }

    const { data: rels } = await supabase.from('shift_releases').select('*').eq('company_id', companyId).eq('status', 'open').order('created_at', { ascending: false });
    setReleases(rels || []);
  }

  async function fetchMessages(empId) {
    const { data } = await supabase.from('messages').select('*').eq('company_id', companyId)
      .or(`sender_id.eq.${empId},recipient_id.eq.${empId}`)
      .order('created_at', { ascending: true });
    setMessages(data || []);
    // Mark as read
    await supabase.from('messages').update({ read: true }).eq('company_id', companyId).eq('sender_id', empId).eq('read', false);
    if (onMessagesRead) onMessagesRead();
  }

  function selectEmployee(emp) {
    setSelected(emp);
    fetchMessages(emp.id);
  }

  async function sendMessage() {
    if (!newMessage.trim() || !selected) return;
    await supabase.from('messages').insert([{
      company_id: companyId,
      sender_id: 0, // 0 = manager
      recipient_id: selected.id,
      message: newMessage.trim(),
      read: false,
    }]);
    setNewMessage('');
    fetchMessages(selected.id);
  }

  async function coverShift(release) {
    // Mark release as covered
    await supabase.from('shift_releases').update({ status: 'covered' }).eq('id', release.id);
    // Send message to released employee
    await supabase.from('messages').insert([{
      company_id: companyId,
      sender_id: 0,
      recipient_id: release.employee_id,
      message: `✅ Your ${release.shift_name} shift on ${release.shift_date} has been covered.`,
      read: false,
    }]);
    fetchAll();
  }

  function getEmployee(id) {
    return employees.find(e => e.id === id);
  }

  function formatTime(ts) {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  function formatDate(ts) {
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const unreadCount = releases.length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4">
        <h1 className="text-sm font-medium text-gray-900">Messages</h1>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setActiveTab('messages')}
            className={`px-3 py-1 text-xs font-medium rounded-md ${activeTab === 'messages' ? 'bg-white text-gray-900' : 'text-gray-400'}`}>
            Messages
          </button>
          <button onClick={() => setActiveTab('releases')}
            className={`px-3 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 ${activeTab === 'releases' ? 'bg-white text-gray-900' : 'text-gray-400'}`}>
            Shift Releases
            {unreadCount > 0 && <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{unreadCount}</span>}
          </button>
        </div>
      </div>

      {activeTab === 'messages' && (
        <div className="flex flex-1 overflow-hidden">
          {/* Employee list */}
          <div className="w-56 border-r border-gray-200 bg-white flex flex-col">
            <div className="p-3 border-b border-gray-100">
              <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Staff</div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {employees.map(emp => {
                const thread = threads.find(t => t.empId === emp.id);
                const unread = thread?.msgs.filter(m => !m.read && m.sender_id === emp.id).length || 0;
                return (
                  <button key={emp.id} onClick={() => selectEmployee(emp)}
                    className={`w-full text-left px-3 py-3 border-b border-gray-100 flex items-center gap-2.5 hover:bg-gray-50 ${selected?.id === emp.id ? 'bg-blue-50' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${emp.color}`}>{emp.initials}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 flex items-center justify-between">
                        {emp.name.split(' ')[0]}
                        {unread > 0 && <span className="bg-blue-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{thread?.latest?.message || 'No messages'}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message thread */}
          <div className="flex-1 flex flex-col bg-gray-50">
            {selected ? (
              <>
                <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${selected.color}`}>{selected.initials}</div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{selected.name}</div>
                    <div className="text-xs text-gray-400">{typeof selected.role === 'string' ? selected.role : (selected.role || []).join(', ')}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
                  {messages.length === 0 && (
                    <div className="text-center text-sm text-gray-400 mt-8">No messages yet. Send one below.</div>
                  )}
                  {messages.map((msg, i) => {
                    const isManager = msg.sender_id === 0;
                    return (
                      <div key={i} className={`flex ${isManager ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs px-3 py-2 rounded-2xl text-sm ${isManager ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                          <div>{msg.message}</div>
                          <div className={`text-xs mt-1 ${isManager ? 'text-blue-200' : 'text-gray-400'}`}>{formatTime(msg.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="bg-white border-t border-gray-200 p-3 flex gap-2">
                  <input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    placeholder="Message..."
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50" />
                  <button onClick={sendMessage}
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-700">Send</button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                Select an employee to message
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'releases' && (
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {releases.length === 0 ? (
            <div className="text-center text-sm text-gray-400 mt-12">No open shift releases</div>
          ) : (
            <div className="flex flex-col gap-3">
              {releases.map((r, i) => {
                const emp = getEmployee(r.employee_id);
                return (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {emp && <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${emp.color}`}>{emp.initials}</div>}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{r.employee_name} released a shift</div>
                          <div className="text-xs text-gray-400">{formatDate(r.created_at)}</div>
                        </div>
                      </div>
                      {r.incentive > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">+${r.incentive} offered</span>
                      )}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div><span className="text-gray-400">Date</span><div className="font-medium text-gray-800 mt-0.5">{r.shift_date}</div></div>
                        <div><span className="text-gray-400">Shift</span><div className="font-medium text-gray-800 mt-0.5">{r.shift_name}</div></div>
                        <div><span className="text-gray-400">Role</span><div className="font-medium text-gray-800 mt-0.5">{r.role}</div></div>
                      </div>
                      {r.reason && <div className="mt-2 text-xs text-gray-500 italic">"{r.reason}"</div>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => coverShift(r)}
                        className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-xs font-medium hover:bg-blue-700">
                        Mark as covered
                      </button>
                      <button onClick={() => { setSelected(emp); setActiveTab('messages'); fetchMessages(emp?.id); }}
                        className="flex-1 border border-gray-200 text-gray-600 rounded-lg py-2 text-xs hover:bg-gray-50">
                        Message {r.employee_name?.split(' ')[0]}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}